import Promise from 'bluebird';
import loglevel from 'loglevel';
import { queryString } from 'lib/format';
import { newRelicEmitter, UNFULFILLED_PROMISE_EVENT} from 'lib/metrics/newRelicEventBus';
import request from './cache/requestCache';
import proxy from './requestProxy';
import fallback from './cache/fallbackCache';
import appConfig from '../../../config';

const log = loglevel.getLogger('PageAPI');

const pageApiConfig = appConfig.pageApi;

const DEFAULT_EXPIRATION = 60;
const DEFAULT_TIMEOUT = 10000;
const ONE_HOUR = 3600;

export const ERROR_CODES = {
    GENERIC_ERROR: 'GENERIC_ERROR',
    PAGE_NOT_CONFIGURED: 'PAGE_NOT_CONFIGURED',
    COUNTRY_NOT_SUPPORTED: 'COUNTRY_NOT_SUPPORTED'
};
// TODO: This could be something more elegant, like a bucket for the linear channels
export const ERROR_PAGE_RESPONSE = {
    page: {
        error: {
            errorCode: ERROR_CODES.GENERIC_ERROR,
            message: 'Page data not found'
        }
    }
};

const ENVIRONMENT_CONFIGURATION = {
    qa: {
        serviceUrl: 'http://watch.product.api.qa.espn.com',
        internalServiceUrl: 'http://watchproductapi.qa.internal.geo.espn.com'
    },
    staging: {
        serviceUrl: 'http://watch.product.api.staging.espn.com',
        internalServiceUrl: 'http://watchproductapi-staging.prod.internal.geo.espn.com'
    },
    prod: {
        serviceUrl: 'http://watch.product.api.espn.com',
        internalServiceUrl: 'http://watchproductapi.prod.internal.geo.espn.com'
    }
};

const DEFAULT_CONFIGURATION = {
    serviceUrl: 'http://watch.product.api.espn.com',
    internalServiceUrl: 'http://watchproductapi.prod.internal.geo.espn.com',
    paths: {
        base: '/api/product/v1/watchespn/web',
        home: '/api/product/v1/watchespn/web/home',
        event: '/api/product/v1/watchespn/web/event',
        video: '/api/product/v1/watchespn/web/video'
    }
};

const config = { ...DEFAULT_CONFIGURATION, ...pageApiConfig };

const getServiceUrl = (env = 'prod') => {
    if (process.browser) {
        var browserEnvironment = global.watchGlobals && global.watchGlobals.env;
        return ENVIRONMENT_CONFIGURATION[browserEnvironment || env].serviceUrl;
    }
    // return ENVIRONMENT_CONFIGURATION[env].internalServiceUrl;
    return ENVIRONMENT_CONFIGURATION[env].serviceUrl;
};

const getPageBaseUrl = (env = 'prod') => {
    return `${getServiceUrl(env)}${config.paths.base}`;
};

export function getPage(name, query = {}, lang, deviceType = 'desktop', countryCode = 'us') {
    const isShortStopLive = (name === 'shortstoplive');
    const ssBust = isShortStopLive ? (Math.floor(Math.random() * 1000)) : null;
    const qs = queryString({ lang, deviceType, ssBust, countryCode });
    const url = `${getPageBaseUrl(query.env)}/${name}${qs}`;

    log.debug(`getPage: ${url}`);

    return request.get(url)
        .expiration(isShortStopLive ? 0 : DEFAULT_EXPIRATION)
        .use(proxy)
        .timeout(DEFAULT_TIMEOUT)
        .then(({ body }) => {
            fallback.set(url, body);
            return body;
        })
        .catch((err) => {
            log.error(`getPage: ${url}`, err.status);
            newRelicEmitter.emit(UNFULFILLED_PROMISE_EVENT, err);
            if (err.status >= 400 && err.status < 500) {
                return (err.body && err.body.page && err.body.page.error) ? err.body : ERROR_PAGE_RESPONSE;
            }
            return fallback.get(url);
        });
}

export function getEvent(params, query = {}, lang, deviceType = 'desktop', countryCode = 'us') {
    // essentially our schema for event page requests
    const eventQuery = {
        airingId: query.airingId,
        id: query.id,
        eventId: query.eventId
    };

    const qs = queryString({ ...eventQuery, lang, deviceType, countryCode });
    const url = `${getServiceUrl(query.env)}${config.paths.event}${qs}`;

    if (!Object.keys(query || {}).length && !params) {
        return Promise.reject(new Error('No params to get an event.'));
    }

    log.debug(`getEvent: ${url}`);

    return request.get(url)
        .use(proxy)
        .timeout(DEFAULT_TIMEOUT)
        .then(({ body }) => {
            fallback.set(url, body);
            return body;
        })
        .catch((err) => {
            log.error(`getEvent: ${url}`, err.originalError);
            newRelicEmitter.emit(UNFULFILLED_PROMISE_EVENT, err);
            return fallback.get(url);
        });
}

// video endpoint is slightly different and uses a path param
export function getVideo(params, query = {}, lang, deviceType = 'desktop', countryCode = 'us') {
    const qs = queryString({ ...query, lang, deviceType, countryCode });
    const pathParams = params ? `/${params}` : '';

    const url = `${getServiceUrl(query.env)}${config.paths.video}${pathParams}${qs}`;

    if (query === '' && params === '') {
        return Promise.reject(new Error('No params to get a video.'));
    }

    log.debug(`getVideo: ${url}`);

    return request.get(url)
        .use(proxy)
        .timeout(DEFAULT_TIMEOUT)
        .then(({ body }) => {
            fallback.set(url, body);
            return body;
        })
        .catch((err) => {
            log.error(`getVideo: ${url}`, err.originalError);
            newRelicEmitter.emit(UNFULFILLED_PROMISE_EVENT, err);
            return fallback.get(url);
        });
}

export function getBucketPage(pathParams, query = {}, lang, deviceType = 'desktop', countryCode = 'us') {
    // essentially our schema for bucket page requests
    const bucketQuery = {
        bucketId: query.bucketId,
        groupId: query.groupId,
        deviceType
    };
    const { groupId = null } = pathParams || {};
    const qs = queryString({ groupId, ...bucketQuery, lang, countryCode });
    const url = `${getPageBaseUrl(query.env)}/bucket${qs}`;

    log.debug(`getBucketPage: ${url}`);

    return request.get(url)
        .use(proxy)
        .timeout(DEFAULT_TIMEOUT)
        .then(({ body }) => {
            fallback.set(url, body);
            return body;
        })
        .catch((err) => {
            log.error(`getBucketPage: ${url}`, err.originalError);
            newRelicEmitter.emit(UNFULFILLED_PROMISE_EVENT, err);
            return fallback.get(url);
        });
}

export function getScoringInfluencers(query = {}) {
    const url = `${getServiceUrl(query.env)}/scoring/influencers?type=personalization`;

    log.debug(`getScoringInfluencers: ${url}`);

    return request.get(url)
        .expiration(ONE_HOUR)
        .use(proxy)
        .timeout(DEFAULT_TIMEOUT)
        .then(({ body }) => {
            const result = body || [];
            fallback.set(url, body);
            return result;
        })
        .catch((err) => {
            log.error('getScoringInfluencers:', err.originalError || err);
            newRelicEmitter.emit(UNFULFILLED_PROMISE_EVENT, err);
            return fallback.get(url);
        });
}

// #TODO: check the format of the url param and reject if invalid
export function getPageByFullUrl(urlArg, deviceType = 'desktop', countryCode = 'us') {
    const qs = queryString({ deviceType, countryCode });
    const url = `${urlArg}${qs}`;
    log.debug(`getUrl: ${url}`);
    if (url) {
        return request.get(url)
            .use(proxy)
            .timeout(DEFAULT_TIMEOUT)
            .then(({ body }) => {
                fallback.set(url, body);
                return body;
            }, (err) => {
                log.error(`getUrl: ${url}`, err.status);
                newRelicEmitter.emit(UNFULFILLED_PROMISE_EVENT, err);
                if (err.status === 404) {
                    return ERROR_PAGE_RESPONSE;
                }
                return fallback.get(url);
            });
    }
    return Promise.reject(new Error('No url supplied'));
}



// WEBPACK FOOTER //
// ./app/lib/api/PageAPI.js